import { useState, useRef, useEffect } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-contact');
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      await fetch('https://readdy.ai/api/form/d7f5aouivmjfhtdrfp10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-stone-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-8">
        <img
          src="https://public.readdy.ai/ai/img_res/fefe52c1c4d8faac7714f3d67cfb037b.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-stone-950/90"></div>

      <div ref={sectionRef} className="max-w-6xl mx-auto px-4 md:px-10 relative z-10">
        {/* Header */}
        <div
          className="reveal-contact text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            Get in Touch
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">
            Connect with{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
              Kevin
            </span>
          </h2>
          <p className="text-stone-400 text-lg max-w-xl mx-auto">
            Whether you have a question about the book, want to share how it moved you, or are interested in speaking engagements — Kevin would love to hear from you.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left: Info */}
          <div
            className="reveal-contact flex-shrink-0 lg:w-80 space-y-8"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            {[
              {
                icon: 'ri-book-open-line',
                title: 'Book Inquiries',
                desc: 'Questions about the Soul Line series, upcoming books, or bulk orders.',
              },
              {
                icon: 'ri-mic-line',
                title: 'Speaking Engagements',
                desc: 'Kevin is available for book clubs, spiritual retreats, and literary events.',
              },
              {
                icon: 'ri-mail-line',
                title: 'General Contact',
                desc: 'Share your thoughts, your story, or simply say hello.',
              },
            ].map((item) => (
              <div key={item.title} className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-amber-400 text-lg`}></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                  <p className="text-stone-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}

            {/* Publisher info */}
            <div className="p-5 rounded-xl bg-stone-900 border border-stone-800">
              <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">Published by</p>
              <p className="text-amber-300 font-semibold text-sm">Leavitt Peak Press</p>
              <p className="text-stone-400 text-xs mt-1">For media and press inquiries, please contact the publisher directly.</p>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="reveal-contact flex-1"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16 bg-stone-900/60 rounded-2xl border border-stone-700/50 px-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-500/20 mb-5">
                  <i className="ri-check-line text-amber-400 text-3xl"></i>
                </div>
                <h3 className="text-white font-serif font-bold text-2xl mb-3">Message Sent!</h3>
                <p className="text-stone-400 text-base leading-relaxed max-w-sm">
                  Thank you for reaching out. Kevin reads every message and will get back to you as soon as he can.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="kevin-hamlett-contact"
                onSubmit={handleSubmit}
                className="bg-stone-900/60 rounded-2xl border border-stone-700/50 p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-2 uppercase tracking-wider">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="Your first name"
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-2 uppercase tracking-wider">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Your last name"
                      className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-stone-400 text-xs font-medium mb-2 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-stone-400 text-xs font-medium mb-2 uppercase tracking-wider">
                    Subject
                  </label>
                  <select
                    name="subject"
                    required
                    className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500/50 transition-colors cursor-pointer"
                  >
                    <option value="">Select a subject...</option>
                    <option value="Book Inquiry">Book Inquiry</option>
                    <option value="Speaking Engagement">Speaking Engagement</option>
                    <option value="Book Club">Book Club</option>
                    <option value="Media / Press">Media / Press</option>
                    <option value="Reader Message">Reader Message</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-stone-400 text-xs font-medium mb-2 uppercase tracking-wider">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Share your thoughts, questions, or how the book has moved you..."
                    className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none focus:border-amber-500/50 transition-colors resize-none"
                  ></textarea>
                  <p className="text-stone-600 text-xs mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-stone-950 font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
